import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Modal, Dialog, Button, Heading, Field, Spacer, InputGroup } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { useForm } from 'react-hook-form';
import { getResolver } from 'src/validation/resolver';
import { TextArea, TemplateInput, InputWithVariable, Select } from 'client/common/form-inputs';
import { templateVariables } from 'src/enums/template-variables';
import { convertToInputUnits, convertToStorageUnits } from 'client/common/units/units';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { initialOperation, updateOperation } from 'store/entities/templates/templates';
import { selectSectionsDataStructure } from 'src/store/entities/sections-data-structure/selector';
import { settingsOperationsSchemaValidator } from 'schemas/ajv-validators';
import { QuantitiesTypes } from 'src/enums/unit-templates';
import { convertToKebabCase } from 'src/common/utils/string-utils.ts';

const OperationModal = ({
  entity,
  templates,
  operation,
  activeSection,
  setModalVisible,
  isAdding,
  depthUnit,
  operations,
  updateOperation,
}) => {
  const { t } = useTranslation();
  const { company: companyId } = useParams();

  const activeOperations = operations.filter((operation) => operation.sectionId === activeSection.sectionId);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'all',
    defaultValues: initialOperation,
    resolver: getResolver(settingsOperationsSchemaValidator),
  });

  useEffect(() => {
    const operationData = operation || initialOperation;
    const convertedOperation = convertToInputUnits(operationData, ['from', 'to'], 'm', depthUnit, true);
    reset(convertedOperation);
  }, [operation?.id, reset]);
  const onSubmit = handleSubmit((data) => {
    const convertedOperation = convertToStorageUnits(data, ['from', 'to'], {
      from: 'm',
      to: 'm',
    });
    updateOperation(convertedOperation, templates, entity, companyId);
  });

  const onClose = () => setModalVisible(false);

  const operationsList = activeOperations.map((operation) => ({
    label: operation.name,
    value: operation.sectionsOperationId,
  }));

  const [isEditing, setIsEditing] = useState(true);
  const { sectionsOperationId } = watch();

  useEffect(() => {
    if (sectionsOperationId && !isEditing) {
      const selectedOption = operationsList.find((option) => option.value === sectionsOperationId);
      setValue('name', selectedOption.label);
      setIsEditing(true);
    }
  }, [sectionsOperationId]);

  const handleOperationChange = (ev) => {
    setIsEditing(false);
    setValue('sectionsOperationId', ev.target.value);
  };

  const baseTestId = `${convertToKebabCase(t(translations.activityModel_newOperation))}-overlay`;

  return (
    <Modal visible centered fullScreen>
      <Dialog
        dialog={{
          heading: watch('name') || t(translations.activityModel_newOperation),
          content: (
            <>
              <form>
                <Field label={t(translations.operation)}>
                  <Select
                    testId={`${baseTestId}-select-operation`}
                    name="sectionsOperationId"
                    control={control}
                    errors={errors}
                    options={operationsList}
                    placeholder={t(translations.activityModel_selectOperation)}
                    onChange={handleOperationChange}
                  />
                </Field>
                <Field label={t(translations.name)}>
                  <TemplateInput
                    name="name"
                    control={control}
                    errors={errors}
                    templateVariables={Object.values(templateVariables)}
                    description={`${t(translations.variablesCanBeUsedForDynamicOperationNames)} ${t(
                      translations.clickToCopyToClipboard,
                    )}`}
                    clickOnTemplate={true}
                  />
                </Field>
                <Heading>{t(translations.depth)}</Heading>
                <Spacer />
                <InputGroup>
                  <Field label={t(translations.from)}>
                    <InputWithVariable
                      name="from"
                      control={control}
                      unitkey={QuantitiesTypes.Depth}
                      unit={depthUnit}
                      errors={errors}
                    />
                  </Field>
                  <Spacer width="var(--padding-sm)" />
                  <Field label={t(translations.to)}>
                    <InputWithVariable
                      name="to"
                      control={control}
                      unitkey={QuantitiesTypes.Depth}
                      unit={depthUnit}
                      errors={errors}
                    />
                  </Field>
                </InputGroup>
                <Field label={t(translations.comments)}>
                  <TextArea name="comments" control={control} errors={errors} rows={5} />
                </Field>
              </form>
            </>
          ),
          footer: (
            <>
              <Button
                label={t(translations.save)}
                colored
                onClick={onSubmit}
                disabled={isAdding}
                testId={`${baseTestId}-button-save`}
              />
              <Button label={t(translations.cancel)} onClick={onClose} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

const mapStateToProps = ({ entities }) => {
  const { operations } = selectSectionsDataStructure(entities);

  return {
    operations,
  };
};

const mapDispatchToProps = { updateOperation };

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(OperationModal), {
  isModal: true,
});

export { Container as OperationModal };
